'use client';
import React, { useState, useEffect } from 'react';
import { CheckCircle2, KanbanSquare, GanttChartSquare, BookOpen } from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';
import { useAuth } from '@/contexts/AuthContext';
import { useSearchParams } from 'next/navigation';

const FEATURES = [
  { icon: <KanbanSquare size={16} />, label: 'Kanban boards & task management' },
  { icon: <GanttChartSquare size={16} />, label: 'Project timelines & Gantt views' },
  { icon: <BookOpen size={16} />, label: 'Team wiki & knowledge base' },
  { icon: <CheckCircle2 size={16} />, label: 'AI-powered research assist' },
];

export default function AuthScreen() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { signInWithGoogle } = useAuth();
  const searchParams = useSearchParams();

  useEffect(() => {
    if (searchParams.get('error') === 'unauthorized_domain') {
      setError('Access denied. Only @into23.com Google accounts are permitted.');
    }
  }, [searchParams]);

  const handleGoogleSignIn = async () => {
    setError(null);
    setIsLoading(true);
    try {
      await signInWithGoogle();
    } catch (err: any) {
      setError(err?.message || 'Sign in failed. Please try again.');
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-background">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col w-1/2 bg-surface-2 border-r border-border p-12 relative overflow-hidden">
        <div className="absolute -top-32 -left-32 w-96 h-96 bg-primary/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-24 -right-24 w-80 h-80 bg-secondary/10 rounded-full blur-3xl pointer-events-none" />

        <div className="flex items-center gap-3 mb-auto">
          <AppLogo size={36} />
          <span className="text-xl font-bold text-foreground">TeamOS</span>
        </div>

        <div className="relative z-10 my-auto">
          <h1 className="text-4xl font-bold text-foreground leading-tight mb-4">
            Your team&apos;s<br />
            <span className="text-gradient-primary">unified workspace</span>
          </h1>
          <p className="text-base text-muted-foreground mb-10 leading-relaxed max-w-sm">
            Replace Basecamp, Box, and Lark with one focused tool built exactly for how your team works.
          </p>

          <div className="space-y-3">
            {FEATURES.map((f) => (
              <div key={`feature-${f.label}`} className="flex items-center gap-3">
                <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center text-primary flex-shrink-0">
                  {f.icon}
                </div>
                <span className="text-sm text-foreground font-medium">{f.label}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-auto">
          <p className="text-xs text-muted-foreground">© 2026 TeamOS. Built for teams that ship.</p>
        </div>
      </div>

      {/* Right auth panel */}
      <div className="flex-1 flex flex-col items-center justify-center p-6 sm:p-10">
        {/* Mobile logo */}
        <div className="flex items-center gap-2 mb-8 lg:hidden">
          <AppLogo size={32} />
          <span className="text-lg font-bold text-foreground">TeamOS</span>
        </div>

        <div className="w-full max-w-sm">
          <div className="mb-8 text-center">
            <h2 className="text-2xl font-bold text-foreground mb-2">Welcome to TeamOS</h2>
            <p className="text-sm text-muted-foreground">Sign in with your into23.com Google account</p>
          </div>

          {error && (
            <div className="flex items-center gap-2 bg-danger/10 border border-danger/30 rounded-lg px-4 py-3 mb-5">
              <p className="text-xs text-danger font-medium">{error}</p>
            </div>
          )}

          <button
            onClick={handleGoogleSignIn}
            disabled={isLoading}
            className="w-full flex items-center justify-center gap-3 bg-white hover:bg-gray-50 border border-gray-300 text-gray-700 font-semibold py-3 rounded-lg text-sm transition-all active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed shadow-sm"
          >
            {isLoading ? (
              <>
                <span className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
                Signing in…
              </>
            ) : (
              <>
                <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
                  <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
                  <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
                  <path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
                  <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
                </svg>
                Sign in with Google
              </>
            )}
          </button>

          <p className="text-center text-xs text-muted-foreground mt-6">
            Access is restricted to <span className="font-semibold text-foreground">@into23.com</span> accounts only.
          </p>
        </div>
      </div>
    </div>
  );
}